import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { useNotification } from '../../../hooks/useNotification';
import {
  usePackEstimateUnits,
  useSavePackEstimateUnits,
  type PackEstimateUnit,
} from '../../../services/billing.service';

type Draft = { code: string; en: string; vi: string; anchorSource: string; costUsd: string };

const toDraft = (unit: PackEstimateUnit): Draft => ({
  code: unit.code,
  en: unit.label.en,
  vi: unit.label.vi,
  anchorSource: unit.anchorSource,
  costUsd: unit.costUsd ? String(unit.costUsd) : '',
});

const toUnit = (draft: Draft): PackEstimateUnit => ({
  code: draft.code,
  label: { en: draft.en.trim(), vi: draft.vi.trim() },
  anchorSource: draft.anchorSource,
  costUsd: draft.costUsd.trim() ? Number(draft.costUsd) : null,
});

const isValid = (draft: Draft) =>
  draft.en.trim() !== '' &&
  draft.vi.trim() !== '' &&
  (draft.costUsd.trim() === '' || Number(draft.costUsd) > 0);

/** Edits the "≈ N turns / messages" estimates shown on credit packs in the user app. */
export const PackEstimateUnitsPanel: React.FC = () => {
  const notification = useNotification();
  const { data, isLoading, error } = usePackEstimateUnits();
  const save = useSavePackEstimateUnits();
  const [drafts, setDrafts] = useState<Draft[]>([]);

  useEffect(() => {
    if (data) setDrafts(data.units.map(toDraft));
  }, [data]);

  const update = (index: number, patch: Partial<Draft>) =>
    setDrafts((current) => current.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  const handleSave = () =>
    save.mutate(drafts.map(toUnit), {
      onSuccess: () => notification.success('Pack estimates saved'),
      onError: (err) => notification.error((err as Error).message || 'Failed to save estimates'),
    });

  if (isLoading) return <CircularProgress />;
  if (error) return <Alert severity="error">{(error as Error).message}</Alert>;

  return (
    <Card>
      <CardContent className="space-y-4">
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Credit pack estimates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Shown to users as an approximate "≈ N units" on each credit pack. The cost per unit is
            measured from recent usage; enter a value to override it. Units with fewer samples than
            the minimum and no override are hidden.
          </Typography>
        </Box>
        {drafts.map((draft, index) => {
          const unit = data?.units[index];
          return (
            <Box key={draft.code} className="grid grid-cols-1 gap-3 md:grid-cols-4">
              <TextField
                label="Label (EN)"
                size="small"
                value={draft.en}
                onChange={(e) => update(index, { en: e.target.value })}
              />
              <TextField
                label="Label (VI)"
                size="small"
                value={draft.vi}
                onChange={(e) => update(index, { vi: e.target.value })}
              />
              <TextField
                label="Cost override (USD / unit)"
                size="small"
                type="number"
                value={draft.costUsd}
                onChange={(e) => update(index, { costUsd: e.target.value })}
                helperText={
                  unit?.measured.avgCostUsd == null
                    ? `Not measured yet (source: ${draft.anchorSource})`
                    : `Measured $${unit.measured.avgCostUsd.toFixed(4)} from ${unit.measured.sampleSize} samples`
                }
              />
              <Typography variant="body2" color="text.secondary" className="self-center">
                Using:{' '}
                {unit?.effectiveCostUsd == null ? 'hidden' : `$${unit.effectiveCostUsd.toFixed(4)}`}
              </Typography>
            </Box>
          );
        })}
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={save.isPending || drafts.length === 0 || !drafts.every(isValid)}
        >
          Save estimates
        </Button>
      </CardContent>
    </Card>
  );
};
